import { Link } from "react-router-dom";
import { fileUrl } from "@/components/UploadButton";
import { categoryLabel } from "@/lib/categories";
import { useCampaign } from "@/context/CampaignContext";
import AfterpayMessage from "@/components/AfterpayMessage";

export default function ProductCard({ product }) {
  const { campaign } = useCampaign();
  const holidayActive = campaign.active_holiday && campaign.active_holiday !== "general";
  const showGift = holidayActive && product.perfect_gift;
  const soldOut = (product.stock ?? 0) < 1;
  const originalPrice = Number(product.price) || 0;
  const onSale = Boolean(
    product.on_sale && product.sale_price != null && Number(product.sale_price) > 0 && Number(product.sale_price) < originalPrice
  );
  const displayPrice = onSale ? Number(product.sale_price) : originalPrice;

  return (
    <Link
      to={`/product/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className={`group block ${soldOut ? "opacity-70 hover:opacity-100 transition-opacity" : ""}`}
    >
      <div className="aspect-[4/5] overflow-hidden bg-[#FFFFFF] border border-[#B2DFDB] mb-4 relative shadow-sm group-hover:shadow-lg transition-shadow">
        {product.images?.[0] && (
          <img
            src={fileUrl(product.images[0])}
            alt={product.name}
            className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${soldOut ? "grayscale-[40%]" : ""}`}
          />
        )}
        {soldOut && (
          <div className="absolute top-3 left-3" data-testid={`sold-badge-${product.slug}`}>
            <span className="bg-[#E0F2F1]/90 text-[#B87333] font-bold text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 border border-[#B87333]/60 font-medium">
              Sold Out
            </span>
          </div>
        )}
        {!soldOut && product.is_special && (
          <div
            className={`absolute ${(!soldOut && (onSale || product.free_shipping)) ? "bottom-3" : "top-3"} left-3`}
            data-testid={`special-badge-${product.slug}`}
          >
            <span className="bg-[#FFF8E1] text-[#B87333] text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 font-black shadow-lg border border-[#B87333]/40 inline-flex items-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              Special Offer
            </span>
          </div>
        )}
        {!soldOut && onSale && (
          <div className="absolute top-3 left-3" data-testid={`sale-badge-${product.slug}`}>
            <span className="bg-[#D32F2F] text-white text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 border border-[#D32F2F] font-semibold shadow-lg">
              Sale
            </span>
          </div>
        )}
        {!soldOut && product.free_shipping && (
          <div
            className={`absolute ${(!soldOut && onSale) ? "top-12" : "top-3"} left-3`}
            data-testid={`free-shipping-badge-${product.slug}`}
          >
            <span className="bg-[#00BFA5] text-white text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 font-bold shadow-lg inline-flex items-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
              Free Shipping
            </span>
          </div>
        )}
        {showGift && (
          <div className={`absolute top-3 ${(!soldOut && onSale) ? 'right-3' : 'left-3'}`} data-testid={`gift-badge-${product.slug}`}>
            <span className="bg-gradient-to-r from-[#D32F2F] to-[#FFB300] text-[#212121] text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 font-semibold shadow-lg">
              🎁 Perfect Gift
            </span>
          </div>
        )}
      </div>
      <div className="flex justify-between items-baseline gap-3">
        <div className="min-w-0">
          <p className="smallcaps mb-1">
            {product.subcategory
              ? `${categoryLabel(product.category)} · ${product.subcategory}`
              : categoryLabel(product.category)}
          </p>
          <p className={`font-display text-xl leading-tight transition-colors ${soldOut ? "text-[#757575]" : "text-[#212121] group-hover:text-[#B87333] font-bold"}`}>
            {product.name}
          </p>
        </div>
        <div className="flex flex-col items-end flex-shrink-0">
          {!soldOut && onSale && (
            <p className="font-display text-base font-bold text-gray-400 line-through leading-none mb-1" data-testid={`orig-price-${product.slug}`}>
              ${originalPrice.toFixed(0)}
            </p>
          )}
          <p
            className={`font-display font-black ${onSale ? "text-2xl" : "text-xl"} leading-none ${soldOut
              ? "text-gray-400 line-through decoration-[#757575]/50"
              : "text-[#212121]"}`}
            data-testid={`price-${product.slug}`}
          >
            ${Number(displayPrice).toFixed(0)}
          </p>
          {!soldOut && (
            <AfterpayMessage price={displayPrice} variant="compact" className="mt-1.5" />
          )}
        </div>
      </div>
    </Link>
  );
}
